/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    FlexRow,
    FlexCol,
    ProgressIndicator,
    Button,
    ButtonGroup,
    NotificationActions,
    NotificationContainer,
    NotFoundPage,
    ContentBlock,
    Stack,
    CartActions,
    StyledModal,
    ModalActions,
    useFlags,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { FacilityAlertBannerContainer } from 'shared-ui/containers/FacilityAlertBannerContainer';
import { bindActionCreators } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import countBy from 'lodash/countBy';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';
import { connect } from 'react-redux';
import { from, Subject, of } from 'rxjs';
import { INVENTORY_ACTIVITY_PASS } from 'ui-field-sales/src/constants/types';
import { retry, map, concatMap, catchError } from 'rxjs/operators';
import { hasCartLimitExceededError } from 'ui-cart';
import { INVENTORY_PARKPASS } from 'ui-cart/src/constants';
import { today } from '@internationalized/date';
import { gnoment } from 'shared-ui/gnoment';
import { withRecaptchaFallback, RecaptchaCheckbox } from 'shared-ui/recaptcha';
import { MAX_PASSES_IN_CART } from '../constants';
import {
    RECAPTCHA_SECTION,
    RECAPTCHA_SCORE,
    RECAPTCHA_CHALLENGE,
    RECAPTCHA_ERROR,
    RECAPTCHA_ERROR2,
} from '../constants/recaptcha';
import { setRecaptchaData } from '../utils/recaptcha';
import {
    memoizeDebounce,
    scrollUp,
    isOffline,
    isPassTypeValid,
    isPassNumberValid,
    useError,
} from '../utils';
import { getPrice, getParentAsset } from '../api';
import FAQModal from '../components/FAQModal';
import PassSelection from '../components/PassSelection';
import HeaderPlaceholder from '../components/HeaderPlaceholder';
import { detailsRoute } from '../../dev/routesArray';
import { isDailyPassType } from '../utils/date_time_utils';

import {
    fetchCart,
    fetchFacilityByIdOrSlug,
    fetchPassTypes,
    fetchReservations,
    createReservations,
    removeReservation,
    validateReservation,
    clearValidationErrors,
} from '../actions';
import { verifyAddress as verifyAddressAction } from '../actions/address';
import {
    getErrors,
    facilityByIdOrSlugSelectors,
    passTypesSelectors,
    reservationsSelectors,
    isLoading,
    isLoaded,
    validationSelectors,
} from '../reducers';

import {
    FacilityProps,
    PassTypeProps,
    ReservationProps,
    ReservationDefaults,
} from '../props';

const LoadingProps = PropTypes.shape({
    facility: PropTypes.bool,
    passTypes: PropTypes.bool,
    reservations: PropTypes.bool,
    facilityByIdOrSlug: PropTypes.bool,
});

export const withFeatureFlagHookHOC = (Component) => {
    return function FeatureFlagThing(props) {
        const { activityPassRecaptchaEnable } = useFlags();
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <Component {...props} recaptchaEnabled={activityPassRecaptchaEnable} />;
    };
};

export class PassSelectionPage extends Component {
    static defaultProps = {
        fetchCart: () => {},
        fetchFacilityByIdOrSlug: () => {},
        loaded: {},
        loading: {},
        reservations: [],
        recaptchaEnabled: false,
    };

    static propTypes = {
        account: PropTypes.object,
        cartReservations: PropTypes.arrayOf(PropTypes.object),
        cartReservationIds: PropTypes.arrayOf(PropTypes.string),
        createReservations: PropTypes.func,
        validateReservation: PropTypes.func,
        facility: FacilityProps,
        fetchCart: PropTypes.func,
        fetchFacilityByIdOrSlug: PropTypes.func,
        fetchPassTypes: PropTypes.func,
        fetchReservations: PropTypes.func,
        loaded: LoadingProps,
        loading: LoadingProps,
        router: COMPAT_ROUTER_PROP_TYPES,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        removeReservation: PropTypes.func,
        clearValidationErrors: PropTypes.func,
        reservations: PropTypes.arrayOf(ReservationProps),
        reservationsError: PropTypes.any,
        showCartLimitExceededModal: PropTypes.func,
        openLoginWithGuestModal: PropTypes.func,
        setNotification: PropTypes.func,
        verifyAddress: PropTypes.func,
        recaptchaEnabled: PropTypes.bool,
        recaptcha: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.gaRef = React.createRef();
        this.gaRef.current = false;

        const { siteId } = props.router.params;

        this.priceSubject = new Subject();
        this.removedReservationIds = [];

        this.state = {
            hasSaveReservationIntent: false,
            needToKnowChecked: false,
            isSubmitting: false,
            parentAsset: [],
            passCounts: {},
            submitAttempted: false,
            lastSubmitAttempt: 0,
            reservationValidStatuses: {},
            reservations: props.reservations.filter(
                ({ facility_id: id }) => id === siteId
            ),
            isAddToCartDisabled: false,
            isRecaptchaModalOpen: false,
        };
    }

    componentDidMount() {
        const { loading, loaded } = this.props;

        const { siteId, resId } = this.props.router.params;
        const searchParams = this.props.router.searchParams;
        const query = Object.fromEntries(searchParams.entries());

        this.loadPageData(loading, loaded, {
            ...query,
            fid: siteId || query.fid,
            rid: resId || query.rid,
        });

        this.priceSubject
            .asObservable()
            .pipe(
                concatMap((reservation) => from(this.getReservationPrice(reservation))),
                retry(5),
                map(({ data }) => data),
                catchError(() => {
                    return of(false);
                })
            )
            .subscribe((pricing) => {
                const reservations = cloneDeep(this.state.reservations);
                const { id, fees, price, total } = pricing;
                const index = reservations.findIndex(({ id: rId }) => rId === id);
                if (index > -1) {
                    Object.assign(reservations[index], {
                        has_price: true,
                        fees,
                        price,
                        total,
                    });
                    if (!isEqual(this.state.reservations, reservations)) {
                        this.setState({ reservations });
                    }
                }
            });
    }

    componentDidUpdate(prevProps) {
        const { account } = this.props;
        const { siteId } = this.props.router.params;
        const { hasSaveReservationIntent } = this.state;
        const newState = {};

        if (!prevProps.account && account && hasSaveReservationIntent) {
            // setState is OK, checking for difference in account and hasSaveReservationIntent
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ hasSaveReservationIntent: false }, this.saveReservation);
        }

        if (
            this.props.reservations.length &&
            !isEqual(this.props.reservations, prevProps.reservations)
        ) {
            newState.reservations = this.props.reservations.filter(
                ({ facility_id: id }) => id === siteId
            );
            newState.passCounts = countBy(this.props.reservations, 'pass_type_id');
            newState.needToKnowChecked = true;
            this.setState((prevState) => ({ ...prevState, ...newState }));
        }
    }

    onPassCountChange = (passTypeId, count) => {
        const passTypes = this.getPassTypes();
        const currentPassType = passTypes.find((item) => item.id === passTypeId);
        const { account } = this.props;
        if (!account && currentPassType?.features['mailing-info'] === true) {
            this.props.openLoginWithGuestModal();
            return;
        }

        const goingUp = count > (this.state.passCounts[passTypeId] || 0);

        let reservations = cloneDeep(this.state.reservations);
        const reservationValidStatuses = cloneDeep(this.state.reservationValidStatuses);

        let passInfo = {};

        if (!isNil(this.props.account)) {
            passInfo = {
                pass_info: {
                    ...ReservationDefaults.pass_info,
                    holder_info: {
                        first_name: this.props.account?.first_name,
                        last_name: this.props.account?.last_name,
                        email: this.props.account?.email,
                    },
                },
            };
        }
        if (goingUp) {
            const newReservation = {
                ...ReservationDefaults,
                id: uniqueId(),
                is_pending: true,
                facility_id: this.getSiteId(),
                pass_type_id: passTypeId,
                ...passInfo,
            };
            newReservation.pass_info.start_date = today().toString();
            newReservation.pass_info.features['start-date'] = today().toString();

            if (!isNil(currentPassType)) {
                const duration =
                    currentPassType.duration === 'seasonal'
                        ? 'season'
                        : currentPassType.duration;
                newReservation.pass_info.duration = duration;

                if (isDailyPassType(currentPassType?.duration)) {
                    newReservation.pass_info.start_date = '';
                    newReservation.pass_info.features['start-date'] = '';
                }
            }
            reservations.push(newReservation);
        } else {
            const oldReservation = reservations
                .concat()
                .reverse()
                .find(({ pass_type_id: ptId }) => ptId === passTypeId);
            if (oldReservation && !oldReservation.is_pending) {
                this.removedReservationIds.push(oldReservation.id);
            }
            reservationValidStatuses[oldReservation.id] = true;
            reservations = reservations.filter(({ id }) => id !== oldReservation.id);
        }

        this.setState((prevState) => ({
            ...prevState,
            passCounts: {
                ...prevState.passCounts,
                [passTypeId]: count,
            },
            reservations,
            reservationValidStatuses,
        }));
    };

    onReservationChange = memoizeDebounce(
        (reservation, reservationIsValid) => {
            // eslint-disable-next-line react/no-access-state-in-setstate -- I think this is a linting false positive
            const reservations = cloneDeep(this.state.reservations);
            const reservationValidStatuses = cloneDeep(
                this.state.reservationValidStatuses
            );
            const index = reservations.findIndex(({ id }) => id === reservation.id);

            let oldFeatures = cloneDeep(reservation.pass_info.features);
            const newFeatures = cloneDeep(reservation.pass_info.features);

            if (index > -1) {
                oldFeatures = reservations[index].pass_info.features;
                reservations[index] = reservation;
                if (!isEqual(oldFeatures, newFeatures)) {
                    this.priceSubject.next(reservation);
                }
            }

            this.setState({
                reservations,
                reservationValidStatuses: {
                    ...reservationValidStatuses,
                    [reservation.id]: reservationIsValid,
                },
            });
        },
        200,
        { resolver: (res) => res.id }
    );

    updateAddToCartState = (isDisabled) => {
        this.setState({
            isAddToCartDisabled: isDisabled,
        });
    };

    notifyError = (e) => {
        const { setNotification } = this.props;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const err = useError(e, 'There was a problem loading the activity passes.');
        setNotification('error', err);
    };

    getReservationPrice = ({
        id,
        pass_type_id,
        features,
        pass_info: { start_date: startDate },
    }) => {
        const { passTypes } = this.props;
        const currentPassType = passTypes?.find((item) => item.id === pass_type_id);
        if (isDailyPassType(currentPassType?.duration) && isEmpty(startDate)) {
            return [];
        }
        return getPrice({
            pass_type_id,
            features: {
                ...features,
                'start-date': startDate,
            },
        })
            .then(({ data }) => ({ data: { ...data, id } }))
            .catch((e) => this.notifyError(e));
    };

    getSiteId = () =>
        this.props.router.params.siteId || this.props.router.searchParams.get('fid');

    getResId = () =>
        this.props.router.params.resId || this.props.router.searchParams.get('rid');

    getPassTypes = () => {
        const hasPassTypes = !!this.props.passTypes?.length;

        if (!hasPassTypes) {
            return this.props.passTypes;
        }

        const passTypes = this.props.passTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return passTypes;
    };

    handleNeedToKnowChecked = (checked) => {
        this.setState({ needToKnowChecked: checked });
    };

    loadPageData = (loading, loaded, query) => {
        if (!loading.facility && !loaded.facility) {
            const facilityId = this.getSiteId();
            const id = facilityId?.replace(/[^0-9a-zA-Z_-]/g, '');
            this.props
                .fetchFacilityByIdOrSlug(id)
                .then((facility) => {
                    getParentAsset(facility.parent_asset_id).then(({ data }) =>
                        this.setState({ parentAsset: data?.results })
                    );
                    if (facility.asset_id && !loading.passTypes && !loaded.passTypes) {
                        this.props.fetchPassTypes(facility.asset_id);
                    }
                })
                .catch((e) => this.notifyError(e));
        }
        const { facility } = this.props;
        const facilityId = this.getSiteId();
        if (facilityId && !facility) {
            this.props.router.navigate('/pass', { replace: true });
            return;
        }
        this.props.fetchCart().catch((e) => this.notifyError(e));

        const { rid } = query;
        if (
            (rid || this.props.cartReservationIds.length) &&
            !loading.reservations &&
            !loaded.reservations
        ) {
            this.props
                .fetchReservations(
                    query.rid ? [query.rid] : this.props.cartReservationIds
                )
                .catch((e) => this.notifyError(e));
        }
    };

    hasErrors = () => {
        const { needToKnowChecked, reservations } = this.state;
        const passTypes = this.getPassTypes();
        let hasErrors = false;

        reservations.forEach((item) => {
            if (
                item?.holder_info?.email_confirmation &&
                item?.holder_info?.email !== item?.holder_info?.email_confirmation
            ) {
                hasErrors = true;
            }

            const passType = passTypes?.find(({ id }) => id === item?.pass_type_id);
            const mailingInfo = item?.features?.['mailing-info'];
            if (
                passType?.features?.['mailing-info'] &&
                mailingInfo?.country === 'USA' &&
                !mailingInfo?.state
            ) {
                hasErrors = true;
            }

            if (
                !isPassTypeValid(
                    item?.features?.discount?.pass_type,
                    item?.features?.discount?.pass_number
                )
            ) {
                hasErrors = true;
            }

            if (
                !isPassNumberValid(
                    item?.features?.discount?.pass_type,
                    item?.features?.discount?.pass_number
                )
            ) {
                hasErrors = true;
            }
        });

        if (!needToKnowChecked) {
            hasErrors = true;
        }

        return hasErrors;
    };

    getRecaptcha = async () => {
        // When a challenge is active the hook returns the solved v2 checkbox
        // token for a V2 (ASSESS) verification. Otherwise it runs the
        // frictionless invisible/score (AUDIT) assessment.
        const { recaptcha } = this.props;

        let recaptchaToken = '';
        try {
            recaptchaToken = await recaptcha.getToken(RECAPTCHA_SECTION);
        } catch (error) {
            /* empty catch */
        }

        const admin = recaptcha.isChallengeRequired
            ? RECAPTCHA_CHALLENGE
            : RECAPTCHA_SCORE;

        return {
            ...setRecaptchaData(recaptchaToken, admin),
        };
    };

    closeRecaptchaModal = () => {
        // "Continue Booking" is only enabled once the checkbox is solved, so
        // closing the modal re-submits the reservation with the challenge token.
        this.setState({ isRecaptchaModalOpen: false }, this.saveReservation);
    };

    saveReservation = async () => {
        this.setState({
            isSubmitting: true,
        });
        const { account } = this.props;
        const passTypes = this.getPassTypes();
        const { needToKnowChecked } = this.state;
        const loggedIn = account != null;
        if (!loggedIn) {
            this.props.openLoginWithGuestModal();
            this.setState({ hasSaveReservationIntent: true, isSubmitting: false });
            return;
        }

        const { reservations } = cloneDeep(this.state);

        // Delete pending `id`s before sending to server.
        // eslint-disable-next-line no-param-reassign
        reservations.filter(({ is_pending }) => is_pending).forEach((r) => delete r.id);

        if (needToKnowChecked) {
            reservations.forEach((reservation) => {
                const passType = passTypes.find(
                    ({ id }) => id === reservation.pass_type_id
                );
                if (get(passType, 'features.accepted', []).includes('need_to_know')) {
                    Object.assign(reservation.features, {
                        accepted: { need_to_know: true },
                    });
                }
                if (!isNil(get(passType, 'features.pass_type_limit', undefined))) {
                    const limit = get(passType, 'features.pass_type_limit', {});
                    Object.assign(reservation.features, { pass_type_limit: limit });
                }
            });
        }

        let hasValidationErrors = false;
        // v2 challenge tokens are single-use. Track whether this submit consumed
        // one so we can clear it afterward and never resubmit a stale token.
        let usedV2Token = false;

        try {
            hasValidationErrors = this.hasErrors();

            if (hasValidationErrors) {
                this.setState({
                    isSubmitting: false,
                });
                throw new Error();
            }

            let recaptcha;
            if (this.props.recaptchaEnabled) {
                usedV2Token = this.props.recaptcha?.isChallengeRequired ?? false;
                recaptcha = await this.getRecaptcha();
            }

            // Will also update reservations.
            await this.props.createReservations(reservations, recaptcha);

            // Reservation succeeded, so tear down any active challenge fallback.
            if (
                this.state.isRecaptchaModalOpen ||
                this.props.recaptcha?.isChallengeRequired
            ) {
                this.props.recaptcha?.resetChallenge();
                this.setState({ isRecaptchaModalOpen: false });
            }

            // Remove any IDs the user removed via the pass counters.
            this.removedReservationIds.reduce(async (promise, id) => {
                await promise;
                return this.props.removeReservation(id);
            }, Promise.resolve());

            setTimeout(
                () => window.location.assign('/cart'),
                1000 * this.removedReservationIds.length
            );
        } catch (e) {
            if (this.props.recaptchaEnabled) {
                const { shouldRetry, isFailed } =
                    this.props.recaptcha.handleRecaptchaError(e);

                // The invisible/score assessment failed and the service is asking
                // for the visible checkbox challenge. handleRecaptchaError has
                // already flipped the hook into v2 mode; open the fallback modal
                // instead of surfacing an error. Solving it re-submits with a
                // V2/ASSESS token.
                if (shouldRetry && !this.state.isRecaptchaModalOpen) {
                    this.setState({
                        isSubmitting: false,
                        isRecaptchaModalOpen: true,
                    });
                    return;
                }

                // reCAPTCHA verification hard-failed. Surface a distinct message
                // depending on which step failed: RECAPTCHA_ERROR2 when the user's
                // checkbox challenge (V2) submission was rejected, otherwise
                // RECAPTCHA_ERROR for the invisible/enterprise assessment failure.
                if (isFailed) {
                    const failedCheckbox = this.props.recaptcha.isChallengeRequired;
                    this.props.recaptcha.resetChallenge();
                    this.setState({
                        submitAttempted: true,
                        lastSubmitAttempt: gnoment().unix(),
                        isSubmitting: false,
                        isRecaptchaModalOpen: false,
                    });
                    this.props.setNotification(
                        'error',
                        failedCheckbox ? RECAPTCHA_ERROR2 : RECAPTCHA_ERROR
                    );
                    scrollUp();
                    return;
                }
            }

            // No-op. Errors will propagate through props.errors
            this.setState({
                submitAttempted: true,
                lastSubmitAttempt: gnoment().unix(),
                isSubmitting: false,
            });
            if (!hasValidationErrors) {
                this.notifyError(e);
            }
            reservations.map((reservation, index) =>
                this.props.validateReservation(index, reservation)
            );
            scrollUp();
            if (Array.isArray(this.props.reservationsError)) {
                const hasCartLimitError = this.props.reservationsError.find((error) =>
                    hasCartLimitExceededError(error?.error_message)
                );
                if (hasCartLimitError) {
                    this.props.showCartLimitExceededModal();
                }
            }
        } finally {
            // reset the challenge if a v2 token was used
            if (usedV2Token) {
                this.props.recaptcha?.resetChallenge();
            }
        }
    };

    removeReservation = async (id) => {
        let reservations = cloneDeep(this.state.reservations);
        const reservationValidStatuses = cloneDeep(this.state.reservationValidStatuses);

        const index = reservations.findIndex(({ id: rid }) => id === rid);
        if (index > -1) {
            // Remove any validation errors
            this.props.clearValidationErrors(index);
            // Remove on the server
            try {
                await this.props.removeReservation(id);
                const res = reservations[index];
                const passTypeId = res.pass_type_id;
                reservationValidStatuses[res.id] = true;
                delete reservations[index];
                reservations = compact(reservations);

                this.setState(
                    (prevState) => ({
                        passCounts: {
                            ...prevState.passCounts,
                            [passTypeId]: prevState.passCounts[passTypeId] - 1,
                        },
                        reservations,
                        reservationValidStatuses,
                    }),
                    () => {
                        const reservationId = this.getResId();
                        if (reservationId) {
                            window.location.assign('/cart');
                        }
                    }
                );
            } catch (e) {
                // No-op. Errors will propagate through props.errors
                this.notifyError(e);
                scrollUp();
            }
        }
    };

    renderProceedButton = () => {
        const { reservations, isAddToCartDisabled, isSubmitting } = this.state;
        const reservationId = this.getResId();
        const hasReservation = reservationId || this.props.reservations.length;
        const hasPendingReservation = reservations.length;
        const buttonText = hasReservation ? 'Save Changes' : 'Add to Cart';
        return (
            <Button
                shouldFitContainer
                size="lg"
                className="activitypass-proceed-button"
                isDisabled={
                    isAddToCartDisabled ||
                    isSubmitting ||
                    !(hasReservation || hasPendingReservation)
                }
                onClick={this.saveReservation}
            >
                {buttonText}
            </Button>
        );
    };

    renderPassSelection = () => {
        const { account, cartReservationIds, facility, loading, loaded } = this.props;
        const { siteId } = this.props.router.params;
        const passTypes = this.getPassTypes();
        const { needToKnowChecked, passCounts, reservations, parentAsset } = this.state;
        const facilityId = this.getSiteId();
        const reservationId = this.getResId();
        const facilities = [facility];
        const faqs = get(facility, 'contents.faq', []);
        const maxTotalGuestsReached =
            cartReservationIds.length + reservations.length >= MAX_PASSES_IN_CART;
        return (
            <PassSelection
                facilities={facilities}
                facility={facility}
                parentAsset={parentAsset}
                facilityId={facilityId}
                handleNeedToKnowChecked={this.handleNeedToKnowChecked}
                loaded={loaded}
                loading={loading}
                loggedIn={account != null}
                maxTotalGuestsReached={maxTotalGuestsReached}
                needToKnowChecked={needToKnowChecked}
                passCounts={passCounts}
                passTypeId={siteId}
                passTypes={passTypes}
                proceedButton={this.renderProceedButton()}
                reservationId={reservationId}
                reservations={reservations}
                submitAttempted={this.state.submitAttempted}
                lastSubmitAttempt={this.state.lastSubmitAttempt}
                onPassCountChange={this.onPassCountChange}
                onRemoveReservation={this.removeReservation}
                onReservationChange={this.onReservationChange}
                updateAddToCartState={this.updateAddToCartState}
                verifyAddress={this.props.verifyAddress}
            >
                <div>
                    <FlexRow
                        hasGutters={false}
                        className="activitypass-proceed-button-wrap"
                    >
                        <FlexCol>
                            <ButtonGroup>
                                {this.renderProceedButton()}
                                {reservationId && (
                                    <Button
                                        shouldFitContainer
                                        size="lg"
                                        appearance="tertiary"
                                        href="/cart"
                                        className="mt-2"
                                    >
                                        {reservationId ? 'Cancel Changes' : 'Cancel'}
                                    </Button>
                                )}
                            </ButtonGroup>
                        </FlexCol>
                    </FlexRow>
                    <FlexRow>
                        <FlexCol>
                            <FAQModal faqs={faqs} />
                        </FlexCol>
                    </FlexRow>
                </div>
            </PassSelection>
        );
    };

    render() {
        const { facility, loading, loaded } = this.props;
        const passTypes = this.getPassTypes();
        const steps = ['Order Details', 'Shopping Cart', 'Payment', 'Confirmation'];

        // Passing additional data to GA
        const wasGaEventFired = this.gaRef;
        const facilityName = facility?.name;
        const facilityId = facility?.id;
        const agencyName = facility?.agency;
        const item_category = INVENTORY_ACTIVITY_PASS;

        // Fire a GA Event to pass the additional info about this page to the GA dataLayer
        if (window.dataLayer && !wasGaEventFired.current && loaded.facilityByIdOrSlug) {
            window.dataLayer.push({
                item_category,
                agencyName,
                facilityId,
                facilityName,
            });
            wasGaEventFired.current = true;
        }

        if (!loading.facilityByIdOrSlug && !facility?.id) {
            return <NotFoundPage />;
        }
        if (!loaded.facilityByIdOrSlug || !loaded.passTypes) {
            return (
                <main className="ap-details-page">
                    <ContentBlock className="mt-5 mb-5">
                        <Stack>
                            <HeaderPlaceholder />
                        </Stack>
                    </ContentBlock>
                </main>
            );
        }
        if (isOffline(passTypes)) {
            return window.location.assign(detailsRoute(facility?.id));
        }

        return (
            <main className="rec-activitypass-page">
                {/* Fire the add_to_cart GA Event */}
                <SendGAEvent
                    eventType="add_to_cart"
                    itemLevel="Inventory"
                    itemCategory={INVENTORY_ACTIVITY_PASS}
                    facilityId={facilityId}
                    facilityName={facilityName}
                />

                <NotificationContainer className={'mb-0 mx-6 mt-4'} />
                <div className="activitypass-order-details-heading">
                    <ProgressIndicator steps={steps} activeStep={1} />
                </div>
                <FacilityAlertBannerContainer />

                <div className="rec-activitypass-section-outer-wrap">
                    <div className="rec-activitypass-wrap">
                        {this.renderPassSelection()}
                    </div>

                    <div className="rec-activitypass-bottom" />
                </div>

                {this.props.recaptchaEnabled && (
                    <StyledModal
                        isOpen={this.state.isRecaptchaModalOpen}
                        shouldCloseOnOverlayClick={false}
                        shouldShowCloseButton={false}
                        contentLabel="Recaptcha"
                        heading="We need to verify that you're a human"
                        ariaHideApp={false}
                        size="md"
                    >
                        <div className="rec-modal-content-wrap">
                            <div className="challenge-wrap">
                                <RecaptchaCheckbox
                                    {...this.props.recaptcha.recaptchaCheckboxProps}
                                    isFallbackOnly
                                    action={RECAPTCHA_SECTION}
                                    size="normal"
                                />
                            </div>
                        </div>

                        <ModalActions>
                            <ButtonGroup isStretchedToFit>
                                <Button
                                    appearance="tertiary"
                                    aria-label="Continue Booking"
                                    onClick={this.closeRecaptchaModal}
                                    disabled={this.props.recaptcha.isV2Invalid}
                                >
                                    Continue Booking
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    </StyledModal>
                )}
            </main>
        );
    }
}

const mapStateToProps = (state) => {
    const errors = getErrors(state, 'reservations', 'facilityByIdOrSlug', 'passTypes');
    const validationErrors = validationSelectors.getValidationErrors(state);

    if (validationErrors.length) {
        errors.validation = validationErrors;
    }

    const reservations = reservationsSelectors.getReservations(state);

    return {
        account: state.auth && state.auth.account && state.auth.account.account,
        cartReservations: state.cart.reservations,
        cartReservationIds: state.cart.reservations
            .filter(({ inventory_type: type }) => type === INVENTORY_PARKPASS)
            .map(({ reservation_id: id }) => id),
        facility: facilityByIdOrSlugSelectors.getFacilityByIdOrSlug(state),
        loaded: isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        loading: isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        passTypes: passTypesSelectors.getPassTypes(state),
        reservations: Array.isArray(reservations) ? reservations : [reservations],
        reservationsError: reservationsSelectors.getReservationsError(state),
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            createReservations,
            fetchCart,
            fetchFacilityByIdOrSlug,
            fetchPassTypes,
            fetchReservations,
            removeReservation,
            validateReservation,
            clearValidationErrors,
            showCartLimitExceededModal: CartActions.showCartLimitExceededModal,
            openLoginWithGuestModal: authModalActions.openLoginWithGuestModal,
            setNotification: NotificationActions.setNotification,
            verifyAddress: verifyAddressAction,
        },
        dispatch
    );

export default withFeatureFlagHookHOC(
    withCompatRouter(
        withRecaptchaFallback(
            connect(mapStateToProps, mapDispatchToProps)(PassSelectionPage)
        )
    )
);

// cspell:ignore parkpass, activitypass, ptid, setstate
